import React from 'react';
import snakeCaseKeys from 'snakecase-keys';
import queryString from 'query-string';
import API from 'shared/constants/api';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import TITLE from 'shared/constants/seoTitles';
import {
  setTargetingArguments,
  setDataLayer,
  setSeoSearchUrl,
  setIsApprovePub,
  seoRequest,
} from 'shared/actions/globals';
import { setAdvertTopCars } from 'shared/actions/advertTopCars';
import { stateDataLayer } from 'shared/modules';
import { getAdvertFromStoreById } from 'shared/helpers/searchResultsRetrievement';
import { resultsThunks } from 'shared/actions/searchForm';
import {
  isLocationCountry,
  getMileageString,
  getPriceWithCurrency,
  getAdvertOgImage,
  getFieldFromRootOrCharacteristics,
  getCapacityUnitTitleById,
} from 'shared/helpers/transportInfo';
import { isLeasingProposalAvailable, getFinupVerification } from 'shared/helpers/leasing/myAuto';

import { hrefSet } from 'shared/actions/partsForm';
import { getAdsContext, getPriceValuation } from 'shared/helpers/adsAndMetrics';
import { generateUrl } from 'shared/helpers/urls';
import { EVENTS } from 'shared/constants/gtmEvents';

import { removeLocaleFromHref } from 'shared/helpers/locale';
import Advert from './Advert';
import Layout from '../../components/Layout';
import LINKS from '../../constants/navigation';
// import { createdDate } from 'shared/helpers/moment';

const REQUIRED_CURRENCY = 'uah';
const similarities = ['transport', 'is_new', 'price', 'category'].join(',');

async function action(data) {
  const { fetch, store, baseUrl, path, translate, params, query } = data;

  const stoRequest = {};
  let stoUrl = API.KATALOG_KOMPANIY_STO;
  let id = params.id.match(/(-[0-9]-)([0-9])+(-[0-9].html)/g);
  if (!id) {
    return null;
  }
  id = id[0].split('-')[2];

  const {
    history: { nextSearchItemIndex },
    globals: { locale: language, isApprovePub },
  } = store.getState();

  if (query.key && !isApprovePub) {
    await fetch(API.ADVERT.APPROVE_PUB(id, query.key)).then(async response => {
      if (response.ok) await store.dispatch(setIsApprovePub(true));
      return response.json();
    });
  } else if (!query.key && isApprovePub) await store.dispatch(setIsApprovePub(false));

  const shortCarInfo = getAdvertFromStoreById(parseInt(id, 10), store);
  const car = await (shortCarInfo ||
    fetch(API.ADVERT.ITEM(id)).then(async response => {
      if (response.ok) {
        return response.json();
      }
      if (response.status === 404) {
        const err = (await response.json()) || {};
        if (err && err.transport && err.transport.slug) {
          return { redirect: `/${err.transport.slug}` };
        }
        return err;
      }
      return {};
    }));
  if (car.redirect) {
    return {
      status: 301,
      redirect: car.redirect,
    };
  }
  if (car.blocked) {
    if (car.category && car.category.slug) {
      return {
        status: 301,
        redirect: `/${car.category.slug}`,
      };
    }
    return {
      status: 301,
      redirect: '/avto',
    };
  }
  if (!(car && car.id)) {
    return null;
  }
  const {
    modification,
    make,
    model,
    year,
    location,
    capacity_unit: capacityUnitId,
    capacity,
    transport,
    color,
    is_new: isNew,
    is_not_cleared: isCustomCleared,
    price,
    // sold,
    // active,
    generation,
    // characteristics: { capacity: newAdvertCapacity },
    characteristics,
    date_created: dateCreated,
    date_publicated: datePublicated,
    hot_date: hotDate,
  } = car;

  if (!car.agency) car.agency = {};
  const dealer = car.agency;

  let videosAboutCarParams = null;

  const hasMakeAndModel = make && make.id && model && !!model.id;
  if (hasMakeAndModel) {
    videosAboutCarParams = {
      rubric: 'video',
      make: make.id,
      model: model.id,
      [year && 'year']: year && year.id,
      [generation && 'generation']: generation && generation.id,
      showInAdvert: 1,
      language,
    };
  }

  const category = getFieldFromRootOrCharacteristics('category', car);
  const engine = getFieldFromRootOrCharacteristics('engine', car);
  const gearbox = getFieldFromRootOrCharacteristics('gearbox', car);
  const drive = getFieldFromRootOrCharacteristics('drive', car);
  const body = getFieldFromRootOrCharacteristics('body', car);
  const capacityAlt = getFieldFromRootOrCharacteristics('capacity', car);

  const agencyId = dealer.id;

  const vipParam = {
    is_top: 1,
    ...(transport && transport.id ? { transport: transport.id } : {}),
  };
  const makeVipParams = {
    ...vipParam,
    ...(make && make.id ? { make: make.id } : {}),
  };
  const dealerVipParams = {
    ...vipParam,
    ...(agencyId ? { dealer: agencyId } : {}),
  };
  const dealerMakeVipParams = {
    ...makeVipParams,
    ...(agencyId ? { dealer: agencyId } : {}),
  };

  const [
    vipParamsRequest,
    makeVipParamsRequest,
    dealerVipParamsRequest,
    dealerMakeVipParamsRequest,
  ] = [vipParam, makeVipParams, dealerVipParams, dealerMakeVipParams].map(
    item => `?${queryString.stringify(item)}`,
  );
  const advertTopCars = store.getState().advertTopCars || {};
  const {
    [dealerMakeVipParamsRequest]: storeDealerSpecialMakeVipCars = null,
    [dealerVipParamsRequest]: storeDealerSpecialVipCars = null,
    [makeVipParamsRequest]: storeSpecialMakeVipCars = null,
    [vipParamsRequest]: storeSpecialVipCars = null,
  } = advertTopCars;

  const [
    specialDealerMakeVipCars,
    specialDealerVipCars,
    specialMakeVipCars,
    specialVipCars,
    spares,
    agencyCars,
    similarCars,
    videosAboutCar,
  ] = await Promise.all([
    agencyId
      ? storeDealerSpecialMakeVipCars ||
        fetch(API.ADVERT.ALL(dealerMakeVipParamsRequest)).then(r => r.json())
      : [],
    agencyId
      ? storeDealerSpecialVipCars ||
        fetch(API.ADVERT.ALL(dealerVipParamsRequest)).then(r => r.json())
      : [],
    storeSpecialMakeVipCars || fetch(API.ADVERT.ALL(makeVipParamsRequest)).then(r => r.json()),
    storeSpecialVipCars || fetch(API.ADVERT.ALL(vipParamsRequest)).then(r => r.json()),
    // spares
    hasMakeAndModel
      ? fetch(
          API.SPARE_PARTS.ALL(
            `?${queryString.stringify(
              snakeCaseKeys({ make: make.id, photos: 1, model: model.id, perPage: 4 }),
            )}`,
          ),
        )
          .then(r => r.json())
          .then(r => (r.results && r.results.length ? r.results : []))
      : [],
    // agencyCars
    agencyId
      ? fetch(API.ADVERT.ALL(`?dealer=${agencyId}&transport=${car.transport.id}`))
          .then(r => r.json())
          .then(response => response.results)
      : [],
    // similarCars
    fetch(API.ADVERT.SIMILAR(id, similarities))
      .then(r => r.json())
      .catch(() => {}),
    // videosAboutCar
    hasMakeAndModel ? fetch(API.JOURNAL.LIST(videosAboutCarParams)).then(r => r.json()) : [],
  ]);

  if (typeof window !== 'undefined') {
    if (!storeDealerSpecialMakeVipCars && specialDealerMakeVipCars) {
      store.dispatch(setAdvertTopCars({ [dealerMakeVipParamsRequest]: specialDealerMakeVipCars }));
    }

    if (!storeDealerSpecialVipCars && specialDealerVipCars) {
      store.dispatch(setAdvertTopCars({ [dealerVipParamsRequest]: specialDealerVipCars }));
    }

    if (!storeSpecialMakeVipCars && specialMakeVipCars) {
      store.dispatch(setAdvertTopCars({ [makeVipParamsRequest]: specialMakeVipCars }));
    }

    if (!storeSpecialVipCars && specialVipCars) {
      store.dispatch(setAdvertTopCars({ [vipParamsRequest]: specialVipCars }));
    }
  }

  const bestOffersCars = [
    ...specialDealerMakeVipCars,
    ...specialDealerVipCars,
    ...specialMakeVipCars,
    ...specialVipCars,
  ];
  const bestOffers = bestOffersCars
    .filter(item => item.id !== parseFloat(id))
    .slice(0, 20)
    .map(offerCar => ({
      link: generateUrl(offerCar),
      id: offerCar.id,
      title: offerCar.title
        ? offerCar.title
        : `${offerCar.make && offerCar.make.title ? offerCar.make.title : ''} ${
            offerCar.model && offerCar.model.title ? offerCar.model.title : ''
          }`,
      year: offerCar.year,
      images: offerCar.photos.length > 0 ? offerCar.photos : [],
      price: getPriceWithCurrency(offerCar.price, REQUIRED_CURRENCY),
      mileage: getMileageString(offerCar.mileage, offerCar.mileage_unit),
      isNew: offerCar.is_new,
      isNegotiablePrice: offerCar.is_negotiable_price,
      fullData: offerCar,
    }));

  const mileage = getMileageString(car.mileage, car.mileage_unit);
  const capacityUnit = getCapacityUnitTitleById(capacityUnitId);
  let transportSeoName = translate('авто');
  if (transport && transport.title && transport.id !== 1) {
    transportSeoName = transport.title;
  }

  const title = TITLE.ADVERT(
    {
      id,

      make,
      model,
      modification,
      location,
      year,
      price: getPriceWithCurrency(car.price, REQUIRED_CURRENCY),
      mileage,
      dealer,
      isNew,
    },
    transportSeoName,
    translate,
  );

  const description = DESCRIPTION.ADVERT(
    {
      id,

      dealer,
      isNew,
      dateCreated,
      datePublicated,
      hotDate,
      body,
      drive,
      modification,
      price: getPriceWithCurrency(car.price, REQUIRED_CURRENCY),
      location,
      make,
      model,
      category,
      year,
      mileage,
      capacity,
      capacityUnit,
      engine,
      gearbox,
      color,
      capacityAlt,
      transport,
      characteristics,
    },
    transportSeoName,
    translate,
  );

  let slug = 'avto';
  if (transport && transport.slug && LINKS.SEARCH[transport.slug]) {
    slug = transport.slug;
  } else if (transport && transport.parents && transport.parents.length) {
    slug = transport.parents[0].slug;
  } else if (transport && transport.slug) {
    slug = transport.slug;
  }

  const url = removeLocaleFromHref(`${baseUrl}/${slug}`.replace('//', '/'));
  const seoQuery = await store.dispatch(seoRequest(`${url}`));
  store.dispatch(setSeoSearchUrl({ ...seoQuery, url }));

  const contextPrice = (Array.isArray(price) && price.filter(a => a.currency === 'uah')[0]) || {};
  const targetingArguments = getAdsContext({
    transport,
    make,
    model,
    category,
    isNew,
    engine,
    location,
    isCustomCleared: typeof isCustomCleared === 'boolean' ? !isCustomCleared : undefined,
    dealer,
    id,
    isCountry: isLocationCountry(location),
    price: getPriceValuation({ from: contextPrice.value }, contextPrice.currency),
  });
  store.dispatch(setTargetingArguments(targetingArguments));
  const ogImage = getAdvertOgImage(car);
  store.dispatch(setDataLayer(stateDataLayer.manipulation({ data: car, pagetype: 'offerdetail' })));
  if (location) {
    stoRequest.location = location.id;
    stoUrl = `${stoUrl}${location.slug}`;
  }
  if (make) {
    stoRequest.makes = make.id;
  }
  /* Autobooking data */
  const queryObj = {
    ...stoRequest,
    per_page: 1,
    top_services: 2,
    shuffle: 1,
  };
  await store.dispatch(
    hrefSet({
      query: queryObj,
      url: stoUrl,
    }),
  );
  if (hasMakeAndModel)
    await store.dispatch(
      resultsThunks.searchFirmsResults(queryObj, { name: 'topFirms', topMakesFilter: false }),
    );
  let leasingParams = null;
  if (isLeasingProposalAvailable(car)) {
    leasingParams = await getFinupVerification({ ...car, url: `${baseUrl}/${path}` }, fetch);
    store.dispatch(setDataLayer(EVENTS.leasing['page-view']));
  }
  return {
    chunks: ['advert'],
    title,
    ogImage,
    description,
    routeName: 'advert',
    type: 'advert',
    shouldSetNoIndex: false,
    shouldSetIndex: true,
    component: (
      <Layout withBranding navLinks={LINKS.SEARCH[slug]} currentRoute={`/${slug}`}>
        <Advert
          key={car.id}
          advertInfo={car}
          isShortView={!!shortCarInfo}
          similarCars={similarCars && similarCars.results && similarCars.results.slice(0, 20)}
          bestOffers={bestOffers}
          agencyCars={agencyCars}
          videosAboutCar={videosAboutCar && videosAboutCar.results}
          spares={spares}
          nextSearchItemIndex={nextSearchItemIndex}
          leasingParams={leasingParams}
          isApprovePub={isApprovePub}
        />
      </Layout>
    ),
  };
}

export default action;



// WEBPACK FOOTER //
// ./src/desktop/routes/advert/index.js